import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Users, Workflow, Megaphone, Search, TrendingUp, Calendar, FileText, UserCheck, Package } from 'lucide-react';
import './AppHeader.css';

/**
 * AppHeader - Top navigation bar with menu icons
 * Design based on Telegram's ChatWindow header pattern
 * Replaces bottom navigation
 * 
 * Features:
 * - Icon-only navigation
 * - Active route highlighting
 * - Minimal, clean design
 * - Sticky to top
 */

interface NavItem {
  path: string;
  icon: React.ReactNode;
  label: string;
}

const navItems: NavItem[] = [
  { path: '/', icon: <Home size={20} />, label: 'Dashboard' },
  { path: '/accounts', icon: <Users size={20} />, label: 'Accounts' },
  { path: '/campaigns', icon: <Megaphone size={20} />, label: 'Campaigns' },
  { path: '/workflows', icon: <Workflow size={20} />, label: 'Workflows' },
  { path: '/group-post-leads', icon: <Search size={20} />, label: 'Collected Leads' },
  { path: '/engagement-post-leads', icon: <TrendingUp size={20} />, label: 'Top Posts' },
  { path: '/joined-groups', icon: <UserCheck size={20} />, label: 'Joined Groups' },
  { path: '/seeding-contents', icon: <Package size={20} />, label: 'Seeding Contents' },
  { path: '/schedules', icon: <Calendar size={20} />, label: 'Schedules' },
  { path: '/reports', icon: <FileText size={20} />, label: 'Reports' },
];

export const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="app-header">
      <div className="app-header__left">
        <h1 className="app-header__title">Business Marketing</h1>
      </div>
      
      <nav className="app-header__nav">
        {navItems.map((item) => (
          <button
            key={item.path}
            className={`app-header__nav-item ${
              isActive(item.path) ? 'app-header__nav-item--active' : ''
            }`}
            onClick={() => navigate(item.path)}
            aria-label={item.label}
            title={item.label}
            {...(item.path === '/schedules' ? { 'data-tour': 'nav-schedules' } : {})}
          >
            {item.icon}
          </button>
        ))}
      </nav>
    </header>
  );
};

export default AppHeader;
